import React, { useEffect, useRef, useState } from "react";
import {
  Building2, TrendingUp, TrendingDown, UserPlus, PieChart, XCircle,
  Inbox, CreditCard, Clock, AlertOctagon, Volume2, VolumeX,
} from "lucide-react";

const ICON_MAP = {
  Building2, TrendingUp, TrendingDown, UserPlus, PieChart, XCircle, Inbox, CreditCard, Clock, AlertOctagon,
};

// v2.0.08 — Beep court via WebAudio API (aucun asset nécessaire)
function playBeep(frequency = 880, durationMs = 200) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
    // Double beep pour attention
    setTimeout(() => {
      try {
        const ctx2 = new AudioCtx();
        const osc2 = ctx2.createOscillator();
        const gain2 = ctx2.createGain();
        osc2.type = "sine";
        osc2.frequency.setValueAtTime(frequency * 1.25, ctx2.currentTime);
        gain2.gain.setValueAtTime(0.15, ctx2.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx2.currentTime + durationMs / 1000);
        osc2.connect(gain2);
        gain2.connect(ctx2.destination);
        osc2.start();
        osc2.stop(ctx2.currentTime + durationMs / 1000);
      } catch {}
    }, durationMs + 60);
  } catch {}
}

export function AdminWidgetLiveBody({ widget, data }) {
  const Icon = ICON_MAP[widget.icon] || Building2;
  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-8 w-8 border border-[#DEE2E6] flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-[#002FA7]" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold flex-1 truncate">{widget.label}</div>
      </div>
      <div className="flex-1 flex flex-col justify-center min-h-0 overflow-auto">
        <AdminWidgetBody widget={widget} data={data} />
      </div>
    </>
  );
}

function AdminWidgetBody({ widget, data }) {
  if (!data) return <div className="text-2xl font-display font-black text-[#DEE2E6]">—</div>;
  switch (widget.key) {
    case "total_entreprises":
      return (
        <div>
          <div className="font-display text-4xl font-black tracking-tighter">{data.value ?? 0}</div>
          <div className="text-[10px] mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(data.by_status || {}).map(([k, v]) => (
              <span key={k} className="text-[#495057]"><b>{v}</b> {k}</span>
            ))}
          </div>
        </div>
      );

    case "mrr":
      return (
        <div>
          <div className="font-display text-3xl font-black tracking-tighter text-[#059669]">
            {(data.value ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold mt-1">
            Monthly Recurring · {data.currency}
          </div>
        </div>
      );

    case "new_signups_7d":
    case "new_signups_30d":
    case "inscriptions_pending":
      return <div className="font-display text-4xl font-black tracking-tighter">{data.value ?? 0}</div>;

    case "churn_rate":
      return (
        <div>
          <div className="font-display text-3xl font-black tracking-tighter" style={{ color: (data.value ?? 0) > 10 ? "#E60000" : "#0A0A0A" }}>
            {data.value ?? 0}%
          </div>
          <div className="text-[10px] text-[#868E96] mt-1">
            {data.cancelled_30d} annulations · {data.active_now} actives
          </div>
        </div>
      );

    case "plans_distribution":
      return (
        <ul className="space-y-0.5 text-xs">
          {(data.items || []).map((p) => (
            <li key={p.key} className="flex justify-between">
              <span className="font-mono text-[#495057]">{p.key}</span>
              <span className="font-bold">{p.count}</span>
            </li>
          ))}
        </ul>
      );

    case "recent_cancellations":
      return (
        <ul className="space-y-1 text-xs">
          {(data.items || []).length === 0 ? (
            <li className="text-[#868E96]">Aucune résiliation récente.</li>
          ) : (data.items || []).map((c) => (
            <li key={c.id} className="border-b border-[#DEE2E6] last:border-b-0 pb-1">
              <div className="font-semibold">{(c.reason || "—").substring(0, 60)}</div>
              <div className="text-[10px] text-[#868E96]">
                {new Date(c.created_at).toLocaleDateString("fr-FR")} · {c.status}
              </div>
            </li>
          ))}
        </ul>
      );

    case "top_revenue_plans":
      return (
        <ul className="space-y-1 text-xs">
          {(data.items || []).map((p) => (
            <li key={p.plan} className="flex justify-between">
              <span className="font-semibold capitalize">{p.plan}</span>
              <span className="font-mono font-bold text-[#059669]">
                {p.revenue_annual.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €
              </span>
            </li>
          ))}
        </ul>
      );

    case "paypal_subscriptions_summary":
      return (
        <div>
          <div className="font-display text-3xl font-black tracking-tighter">{data.total ?? 0}</div>
          <div className="text-[10px] text-[#868E96] mt-1">
            <span className="text-[#059669] font-bold">{data.active}</span> ACTIVE ·{" "}
            <span className="text-[#D97706] font-bold">{data.pending}</span> pending
          </div>
        </div>
      );

    case "scheduler_health":
      return (
        <ul className="space-y-0.5 text-[11px] font-mono">
          {(data.runs || []).slice(0, 5).map((r, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${r.ok ? "bg-[#059669]" : "bg-[#E60000]"}`} />
              <span className="text-[#868E96]">{new Date(r.started_at).toLocaleTimeString("fr-FR")}</span>
              <span className="font-semibold truncate">{r.job_id}</span>
            </li>
          ))}
        </ul>
      );

    case "live_alerts": {
      return <LiveAlertsWidgetBody data={data} />;
    }

    default:
      return <div className="text-xs text-[#868E96]">—</div>;
  }
}

const MUTE_KEY = "pag_live_alerts_muted";
const LAST_ALERT_KEY = "pag_live_alerts_last_id";

function LiveAlertsWidgetBody({ data }) {
  const items = data.items || [];
  const active = data.active_auto_incidents || 0;
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const lastSeenRef = useRef(localStorage.getItem(LAST_ALERT_KEY));

  useEffect(() => {
    if (items.length === 0) return;
    const first = items[0];
    const id = `${first.at}-${first.service}`;
    if (lastSeenRef.current !== id) {
      if (!muted && lastSeenRef.current !== null) {
        // Ne joue le beep qu'à partir du 2ème refresh (évite le beep au premier chargement)
        playBeep();
      }
      lastSeenRef.current = id;
      localStorage.setItem(LAST_ALERT_KEY, id);
    }
  }, [items, muted]);

  const toggleMute = () => {
    const nv = !muted;
    setMuted(nv);
    localStorage.setItem(MUTE_KEY, nv ? "1" : "0");
    if (!nv) playBeep(660, 100); // Test rapide quand on active le son
  };

  if (items.length === 0 && active === 0) {
    return (
      <div className="text-xs relative">
        <div className="text-[#059669] font-bold text-sm">✓ Tous services OK</div>
        <div className="text-[10px] text-[#868E96] mt-1">Aucune erreur en {data.window || "24h"}</div>
        <button
          type="button"
          onClick={toggleMute}
          data-testid="live-alerts-mute"
          className="absolute top-0 right-0 text-[#868E96] hover:text-[#0A0A0A]"
          title={muted ? "Son coupé — cliquez pour activer" : "Son actif — cliquez pour couper"}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5 relative" data-testid="live-alerts-active">
      <button
        type="button"
        onClick={toggleMute}
        data-testid="live-alerts-mute"
        className="absolute top-0 right-0 text-[#868E96] hover:text-[#0A0A0A]"
        title={muted ? "Son coupé — cliquez pour activer" : "Son actif — cliquez pour couper"}
      >
        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>
      {active > 0 && (
        <div className="text-[10px] uppercase tracking-wider font-bold bg-[#E60000] text-white px-2 py-0.5 inline-block">
          {active} incident{active > 1 ? "s" : ""} auto actif{active > 1 ? "s" : ""}
        </div>
      )}
      <ul className="space-y-0.5 text-[11px]">
        {items.slice(0, 3).map((e, i) => (
          <li key={i} className="border-l-2 border-[#E60000] pl-2">
            <div className="flex items-center gap-1">
              <span className="font-mono text-[9px] text-[#868E96]">{new Date(e.at).toLocaleTimeString("fr-FR")}</span>
              <span className="font-bold text-[10px] text-[#E60000] uppercase tracking-wide">{e.service}</span>
            </div>
            <div className="text-[10px] text-[#495057] truncate">{e.operation}: {(e.error || "").slice(0, 60)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
